<div class="min-vh-100 d-flex align-items-center justify-content-center bg-light py-5">
  <div class="card shadow-sm w-100" style="max-width: 560px;">
    <div class="card-body p-4">

      <!-- Header -->
      <div class="text-center mb-4">
        <h3 class="fw-bold mb-1">{{ "register.Title" | translate }}</h3>
        <p class="text-muted small">{{ "register.Subtitle" | translate }}</p>
        <div *ngIf="isTenantLoading" class="mt-2">
          <span class="spinner-border spinner-border-sm text-primary" role="status"></span>
        </div>
        <div *ngIf="!isTenantLoading && tenantName" class="mt-2">
          <span class="badge bg-primary-subtle text-primary border border-primary-subtle px-3 py-2 fs-6">
            <i class="bi bi-building me-1"></i>{{ tenantName }}
          </span>
        </div>
      </div>

      <!-- Step 1: Role selection -->
      <ng-container *ngIf="step === 1">
        <h5 class="mb-3 text-center">{{ "register.IAmA" | translate }}</h5>
        <div class="row g-3 mb-3">
          <div class="col-12" *ngFor="let r of roles">
            <button
              type="button"
              class="btn btn-outline-primary w-100 py-3 d-flex align-items-center gap-3"
              (click)="selectRole(r.value)">
              <i class="bi {{ r.icon }} fs-4"></i>
              <span class="fw-semibold">{{ r.labelEn }} / {{ r.labelAr }}</span>
            </button>
          </div>
        </div>
        <div class="text-center mt-3">
          <a routerLink="/account/login" class="text-muted small">{{ "register.AlreadyHaveAccount" | translate }}</a>
        </div>
      </ng-container>

      <!-- Step 2: Registration form -->
      <ng-container *ngIf="step === 2">
        <div class="d-flex align-items-center mb-3 gap-2">
          <button type="button" class="btn btn-sm btn-outline-secondary" (click)="backToRoleStep()">
            <i class="bi bi-arrow-left"></i>
          </button>
          <span class="fw-semibold">
            {{ "register.RegisteringAs" | translate }}
            <span class="badge bg-primary">{{ selectedRole }}</span>
          </span>
        </div>

        <form [formGroup]="registerForm" (ngSubmit)="onSubmit()" novalidate autocomplete="off">

          <div class="row g-2">

            <!-- Full Name Arabic -->
            <div class="col-12 col-md-6">
              <label class="form-label">{{ "register.FullNameAr" | translate }}</label>
              <input type="text" class="form-control" formControlName="fullNameAr"
                [class.is-invalid]="fieldInvalid('fullNameAr')" placeholder="الاسم الكامل" dir="rtl">
              <div class="invalid-feedback">{{ "register.ErrMin3" | translate }}</div>
            </div>

            <!-- Full Name English -->
            <div class="col-12 col-md-6">
              <label class="form-label">{{ "register.FullNameEn" | translate }}</label>
              <input type="text" class="form-control" formControlName="fullNameEn"
                [class.is-invalid]="fieldInvalid('fullNameEn')" [placeholder]="'register.FullNamePlaceholder' | translate">
              <div class="invalid-feedback">{{ "register.ErrMin3" | translate }}</div>
            </div>

            <!-- Email -->
            <div class="col-12 col-md-6">
              <label class="form-label">{{ "register.Email" | translate }}</label>
              <input type="email" class="form-control" formControlName="email"
                [class.is-invalid]="fieldInvalid('email')" [placeholder]="'register.EmailPlaceholder' | translate">
              <div class="invalid-feedback">{{ "register.ErrEmail" | translate }}</div>
            </div>

            <!-- Phone -->
            <div class="col-12 col-md-6">
              <label class="form-label">{{ "register.PhoneNumber" | translate }}</label>
              <input type="tel" class="form-control" formControlName="phoneNumber"
                [class.is-invalid]="fieldInvalid('phoneNumber')" [placeholder]="'register.PhonePlaceholder' | translate">
              <div class="invalid-feedback">{{ "register.ErrPhone" | translate }}</div>
            </div>

            <!-- Date of Birth -->
            <div class="col-12 col-md-6">
              <label class="form-label">{{ "register.DateOfBirth" | translate }}</label>
              <input type="date" class="form-control" formControlName="dateOfBirth"
                [class.is-invalid]="fieldInvalid('dateOfBirth')">
              <div class="invalid-feedback">{{ "register.ErrDob" | translate }}</div>
            </div>

            <!-- Gender -->
            <div class="col-12 col-md-6">
              <label class="form-label">{{ "register.Gender" | translate }}</label>
              <select class="form-select" formControlName="genderId"
                [class.is-invalid]="fieldInvalid('genderId')">
                <option value="">{{ "register.Select" | translate }}</option>
                <option *ngIf="isGendersLoading" disabled>{{ "app.Loading" | translate }}</option>
                <option *ngFor="let g of genders" [value]="g.id">{{ g.nameEn }}</option>
              </select>
              <div class="invalid-feedback">{{ "register.ErrGender" | translate }}</div>
            </div>

            <!-- Grade (Student only) -->
            <ng-container *ngIf="selectedRole === 'Student'">
              <div class="col-12 col-md-6">
                <label class="form-label">{{ "register.Grade" | translate }}</label>
                <select class="form-select" formControlName="gradeId"
                  [class.is-invalid]="fieldInvalid('gradeId')">
                  <option value="">{{ "register.SelectGrade" | translate }}</option>
                  <option *ngIf="isGradesLoading" disabled>{{ "app.Loading" | translate }}</option>
                  <option *ngFor="let g of grades" [value]="g.id">{{ g.nameEn || g.nameAr }}</option>
                </select>
                <div class="invalid-feedback">{{ "register.ErrGrade" | translate }}</div>
              </div>

              <div class="col-12 col-md-6">
                <label class="form-label">{{ "register.ParentPhoneNumber" | translate }}</label>
                <input type="tel" class="form-control" formControlName="parentPhoneNumber"
                  [class.is-invalid]="fieldInvalid('parentPhoneNumber')" [placeholder]="'register.PhonePlaceholder' | translate">
                <div class="invalid-feedback">{{ "register.ErrParentPhone" | translate }}</div>
              </div>
            </ng-container>

            <!-- Divider -->
            <div class="col-12"><hr class="my-1"></div>

            <!-- Username -->
            <div class="col-12">
              <label class="form-label">{{ "register.Username" | translate }}</label>
              <input type="text" class="form-control" formControlName="userName"
                [class.is-invalid]="fieldInvalid('userName')" [placeholder]="'register.UsernamePlaceholder' | translate"
                autocomplete="off">
              <div class="invalid-feedback">{{ "register.ErrMin3" | translate }}</div>
            </div>

            <!-- Password -->
            <div class="col-12 col-md-6">
              <label class="form-label">{{ "register.Password" | translate }}</label>
              <input type="password" class="form-control" formControlName="password"
                [class.is-invalid]="fieldInvalid('password')" [placeholder]="'register.PasswordPlaceholder' | translate"
                autocomplete="new-password">
              <div class="invalid-feedback">{{ "register.ErrMin8" | translate }}</div>
            </div>

            <!-- Confirm Password -->
            <div class="col-12 col-md-6">
              <label class="form-label">{{ "register.ConfirmPassword" | translate }}</label>
              <input type="password" class="form-control" formControlName="confirmPassword"
                [class.is-invalid]="fieldInvalid('confirmPassword') || (registerForm.errors?.['passwordMismatch'] && registerForm.get('confirmPassword')?.touched)"
                [placeholder]="'register.ConfirmPasswordPlaceholder' | translate" autocomplete="new-password">
              <div class="invalid-feedback">{{ "register.ErrMismatch" | translate }}</div>
            </div>

          </div><!-- /row -->

          <div class="d-grid mt-3">
            <button type="submit" class="btn btn-primary" [disabled]="isLoading">
              <span *ngIf="isLoading" class="spinner-border spinner-border-sm me-1"></span>
              {{ isLoading ? 'Submitting…' : 'Submit Registration' }}
            </button>
          </div>

          <div class="text-center mt-2">
            <a routerLink="/account/login" class="text-muted small">{{ "register.AlreadyHaveAccount" | translate }}</a>
          </div>

        </form>
      </ng-container>

    </div>
  </div>
</div>
